import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import axios from "axios";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

// ==========================
// FETCH ELIGIBLE CLUBS BY PHONE
// ==========================
export const fetchEligibleClubsByPhone =
  createAsyncThunk(
    "group/fetchEligibleClubsByPhone",
    async (
      phone_number: string,
      { rejectWithValue }
    ) => {
      try {
        const res = await axios.get(
          `${API_URL}/group/eligible-clubs/${encodeURIComponent(
            phone_number
          )}`
        );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data?.message ||
            "Failed to fetch eligible clubs"
        );
      }
    }
  );

// ==========================
// JOIN SELECTED CLUBS
// ==========================
export const joinSelectedClubs =
  createAsyncThunk(
    "group/joinSelectedClubs",
    async (
      payload: {
        user_id: number;
        clubs: {
          contact_id: number;
        }[];
      },
      { rejectWithValue }
    ) => {
      try {
        const res = await axios.post(
          `${API_URL}/group/join-club`,
          payload
        );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data?.message ||
            "Failed to join clubs"
        );
      }
    }
  );

interface GroupState {
  eligibleGroups: any[];
  loading: boolean;
  joining: boolean;
  joinedBefore: boolean;
  error: string | null;
}

const initialState: GroupState = {
  eligibleGroups: [],
  loading: false,
  joining: false,
  joinedBefore: false,
  error: null,
};

const groupSlice = createSlice({
  name: "group",

  initialState,

  reducers: {
    removeJoinedClubs: (
      state,
      action
    ) => {
      const joinedContactIds =
        action.payload;

      state.eligibleGroups =
        state.eligibleGroups.filter(
          (club) =>
            !joinedContactIds.includes(
              club.contact_id
            )
        );

      state.joinedBefore = true;
    },

    resetEligibleGroups: (
      state
    ) => {
      state.eligibleGroups = [];
      state.loading = false;
      state.joining = false;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(
        fetchEligibleClubsByPhone.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        fetchEligibleClubsByPhone.fulfilled,
        (state, action) => {
          state.loading = false;

          state.eligibleGroups =
            Array.isArray(
              action.payload?.data
            )
              ? action.payload.data
              : [];
        }
      )

      .addCase(
        fetchEligibleClubsByPhone.rejected,
        (state, action) => {
          state.loading = false;
          state.eligibleGroups = [];
          state.error =
            action.payload as string;
        }
      )

      .addCase(
        joinSelectedClubs.pending,
        (state) => {
          state.joining = true;
          state.error = null;
        }
      )

      .addCase(
        joinSelectedClubs.fulfilled,
        (state) => {
          state.joining = false;
          state.joinedBefore = true;
        }
      )

      .addCase(
        joinSelectedClubs.rejected,
        (state, action) => {
          state.joining = false;
          state.error =
            action.payload as string;
        }
      );
  },
});

export const {
  removeJoinedClubs,
  resetEligibleGroups,
} = groupSlice.actions;

export default groupSlice.reducer;
